/**
 * What a page should SAY when several of its widgets fail at once.
 *
 * The workspace renders a dozen independent queries. When the gateway's rate
 * limiter kicked in, every one of them failed at the same instant and each
 * rendered its own identical "Data unavailable" — which reads as a total
 * outage, gives no hint that it is transient, and offers no way back except
 * guessing when to reload (QA ledger AUTH-004, ORD-009).
 *
 * The fix is not a better per-widget message: it is saying ONCE, at the top of
 * the page, what actually happened and what to do about it.
 */
import { accessDenialReason, apiErrorStatus } from '@lognai/plugin-sdk';

export type FailureKind = 'none' | 'rate-limited' | 'permission' | 'server' | 'unknown';

export interface FailureSummary {
  kind: FailureKind;
  /** How many of the page's widgets failed. */
  count: number;
  /** Headline for the page-level banner. */
  title: string;
  /** What the reader should do. */
  detail: string;
  /** Whether retrying now could plausibly work. */
  retryable: boolean;
}

const NONE: FailureSummary = { kind: 'none', count: 0, title: '', detail: '', retryable: false };

/**
 * Classify a page's failures by their most actionable cause.
 *
 * Rate limiting wins over everything: it explains ALL the failures at once and
 * it is the one the reader can actually act on by waiting. A permissions
 * refusal is reported second because it will not change on retry.
 */
export const summariseFailures = (errors: readonly unknown[]): FailureSummary => {
  const present = errors.filter(Boolean);
  if (present.length === 0) return NONE;

  const statuses = present.map(apiErrorStatus);
  const count = present.length;
  const widgets = `${count} ${count === 1 ? 'panel' : 'panels'}`;

  if (statuses.some((s) => s === 429)) {
    return {
      kind: 'rate-limited',
      count,
      title: 'Too many requests in a short time',
      detail: `Your dashboard is being rate limited, so ${widgets} could not load. This clears on its own — wait a moment and try again.`,
      retryable: true,
    };
  }

  if (statuses.some((s) => s === 401 || s === 403)) {
    const moduleOff = present.some((e) => accessDenialReason(e)?.kind === 'module');
    return {
      kind: 'permission',
      count,
      title: 'Some panels are not available to your account',
      detail: moduleOff
        ? `${widgets} could not load because their module is not enabled for you in this company. Ask a company administrator to assign you a role here.`
        : `${widgets} could not load because your account does not have access to that data. Ask an administrator if you expected to see it.`,
      retryable: false,
    };
  }

  if (statuses.some((s) => typeof s === 'number' && s >= 500)) {
    return {
      kind: 'server',
      count,
      title: 'The server could not answer',
      detail: `${widgets} could not load. Try again — if it keeps happening, report it.`,
      retryable: true,
    };
  }

  return {
    kind: 'unknown',
    count,
    title: `${count === 1 ? 'A panel' : 'Some panels'} could not load`,
    detail: 'The request did not complete. Try again.',
    retryable: true,
  };
};

/** The one-line reason for a single widget's own error state. */
export const widgetFailureReason = (error: unknown): string => {
  const status = apiErrorStatus(error);
  if (status === 429) return 'Rate limited — this clears on its own.';
  const denial = accessDenialReason(error);
  if (denial?.kind === 'module') return 'This module is not enabled for you in this company.';
  if (denial?.kind === 'permission') return `Your role lacks the permission "${denial.permission}".`;
  if (status === 401 || status === 403) return 'Your account does not have access to this data.';
  if (typeof status === 'number' && status >= 500) return 'The server could not answer.';
  return 'The request did not complete.';
};

/** The same reason, short enough for a KPI tile's hint line. */
export const widgetFailureHint = (error: unknown): string => {
  const status = apiErrorStatus(error);
  if (status === 429) return 'Rate limited';
  if (accessDenialReason(error)?.kind === 'module') return 'Module off';
  if (status === 401 || status === 403) return 'No access';
  if (typeof status === 'number' && status >= 500) return 'Server error';
  return 'Could not load';
};
